<script lang="ts">
  import classnames from 'classnames';
  // Utils
  import { getURLFromContentfulAsset } from '$lib/utils/assets-utils';
  import { dateFullMonthFormat, textEllipsisWithHTML } from '$lib/utils/formatter';
  // Contentful
  import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
	import type { INews } from '$lib/utils/contentful/types';

  export let article: INews;
</script>

{#if article}
  <a
    class={classnames('article-card', $$props.class)}
    href="/news/{article.fields.slug}"
  >
    <div class="article-grid">
      <div class="article-image-wrapper">
        <!-- Image Specs -->
        <!-- Max width: 1440px -->
        <!-- Ratio: 2:1 (Mobile) Free (Desktop) -->
        <img
          src={getURLFromContentfulAsset(article.fields.heroImage)}
          alt={article.fields.heroImage && article.fields.heroImage.fields.description ? article.fields.heroImage.fields.description : ''}
          class="article-image object-cover object-center"
        />
      </div>
      <div class="article-content">
        <p class="article-subtitle">
          {dateFullMonthFormat(article.fields.publishedDate)}
        </p>
        <span class="article-link">
          <h3 class="article-title">{article.fields.title}</h3>
        </span>
  
        <p class="article-abstract">
          {@html textEllipsisWithHTML(documentToHtmlString(article.fields.content), 80)}
        </p>
      </div>
    </div>
  </a>
{/if}

<style type="text/scss" lang="scss" scoped>
  .article-card {
    --article-height: 400px;
    @apply bg-transparent;
    @apply relative;
    @apply md:text-white md:text-opacity-70;
  }
  .article-grid {
    @apply grid grid-cols-default;
    @apply gap-x-grid-default;
    @apply w-full;
    @apply md:page-margin md:page-max-width;
    @apply z-3;

    @screen md {
      height: var(--article-height);
    }
  }
  .article-subtitle {
    @apply uppercase tracking-wider font-semibold;
    @apply text-sm md:text-base;
    @apply text-gray-600;
    @apply md:text-white md:text-opacity-70;
  }
  .article-title {
    @apply text-2xl md:text-6xl leading-snug md:leading-tighter;
    @apply font-medium font-serif;
    @apply text-gray-900;
    @apply md:text-white;
  }
  .hero-standard-text {
    @apply md:text-white;
  }
  .article-subtitle + .article-title {
    @apply mt-3;
  }

  .article-title + .article-action {
    @apply mt-5;
  }

  .article-content {
    @apply relative;
    @apply col-span-full;
    @apply page-margin py-6 md:p-0;
    @apply md:row-start-1 md:col-start-1 md:col-span-7 lg:col-start-1 lg:col-span-5;
    @apply md:self-center;

    .long-content & {
      @apply md:row-start-1 md:col-start-1 md:col-span-7 lg:col-start-1 lg:col-span-6;
    }
  }

  .article-image-wrapper {
    @apply md:-page-margin;
    @apply relative;
    @apply col-span-full;
    @apply md:row-start-1;

    /** Fix aspect ratio plugin */
    --tw-aspect-w: 2;
    --tw-aspect-h: 1;
    padding-bottom: calc(var(--tw-aspect-h) / var(--tw-aspect-w) * 100%);

    > * {
      @apply absolute w-full h-full;
      @apply inset-0;
    }

    @screen md {
      /* Remove aspect ratio */
      padding-bottom: 0;
    }

    /* Bottom gradient in the mobile version */
    &:before {
      content: '';
      @apply md:hidden;
      @apply bg-gradient-to-t from-black to-transparent opacity-10;
      @apply absolute inset-x-0 bottom-0;
      @apply h-[60px];
      @apply z-1;
    }

    /*  Dark Overlay for the brighter images */
    &::after {
      content: '';
      @apply hidden md:block;
      @apply absolute inset-0;
      @apply bg-gray-600 opacity-90;
      @apply lg:bg-gradient-to-r lg:from-gray-600 lg:to-gray-300 lg:opacity-100;
      @apply mix-blend-multiply;
    }
  }

  .article-image {
    @apply object-cover object-center;
    @screen md {
      width: 100%;
      height: var(--article-height);
    }
  }

  .article-abstract {
    @apply mt-3 md:mt-8;
    @apply text-sm md:text-base;
    @apply overflow-ellipsis overflow-hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
</style>
